import isNil from 'lodash/isNil';

import { ECexBotFilter } from 'types/bots';
import { IRange } from 'web3';
import { ECexName } from 'web3';

import { BalanceChartPoint, ICexChartsPoint, ICexVWAPPoint, IChartItem } from './models';

import { AApiBase } from '../apiBase/AApiBase';
import { metricsAxios, proxyAxios } from '../apiBase/axios';
import type { IBaseResponse } from '../apiBase/IBaseResponse';
import { mapError } from '../apiBase/mapError';
import { mapResponse } from '../apiBase/mapResponse';

interface IGetBalancesResponse {
  items: IChartItem[];
  total_items: number;
}

export type SolCPMMTimeframe = '1m' | '5m' | '15m' | '1h';

export interface ISolCPMMCandle {
  close: string;
  high: string;
  low: string;
  open: string;
  start_at: string;
  volume_quote: string;
}

export interface IGetSolCPMMCandlesResponse {
  items: ISolCPMMCandle[];
  total_items: number;
}

export interface ISolMeteoraTrade {
  raw_event_id: number;
  pair_id: number;
  pool_address: string;
  decoder_version: string;
  signature: string;
  slot: number;
  transaction_index: number;
  instruction_index: number;
  ordinal: number;
  block_time: string;
  base_input: boolean;
  base_amount: string;
  quote_amount: string;
  price: string;
}

export interface IGetSolMeteoraTradesResponse {
  items: ISolMeteoraTrade[];
  next_cursor?: string;
}

class ApiCharts extends AApiBase {
  protected baseUrl = '/api';
  protected withHeader = true;

  private static __instance: ApiCharts | undefined;
  private static get instance(): ApiCharts {
    if (ApiCharts.__instance === undefined) {
      ApiCharts.__instance = new ApiCharts();
    }
    return ApiCharts.__instance;
  }

  public static getBalances = async ({
    projects_ids,
    pair_ids,
    cex_pair_ids,
    token_ids,
    ranges,
    timeframe,
  }: {
    projects_ids?: number[];
    pair_ids?: number[];
    cex_pair_ids?: number[];
    token_ids?: number[];
    ranges: (IRange | string)[];
    timeframe?: string;
  }) => {
    try {
      const urlParams = new URLSearchParams();
      if (projects_ids && projects_ids.length !== 0) {
        for (const projectId of projects_ids) {
          urlParams.append('project_ids', projectId.toString());
        }
      }

      if (pair_ids && pair_ids.length !== 0) {
        for (const pairId of pair_ids) {
          urlParams.append('pair_ids', pairId.toString());
        }
      } else {
        //TODO remove this shit
        urlParams.append('pair_ids', '');
      }

      if (cex_pair_ids && cex_pair_ids.length !== 0) {
        for (const cexPairId of cex_pair_ids) {
          urlParams.append('cex_pair_ids', cexPairId.toString());
        }
      } else {
        //TODO remove this shit
        urlParams.append('cex_pair_ids', '');
      }

      if (token_ids && token_ids.length !== 0) {
        for (const tokenId of token_ids) {
          urlParams.append('token_ids', tokenId.toString());
        }
      }

      for (const range of ranges) {
        urlParams.append('range', range.toString());
      }

      if (timeframe) {
        urlParams.append('timeframe', timeframe);
      }

      const response = await ApiCharts.instance.get<IBaseResponse<IGetBalancesResponse>>(
        '/charts/balances' + '?' + urlParams.toString(),
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<IGetBalancesResponse>({ error });
    }
  };

  public static getGeckoterminalChart = async ({
    network,
    pool_address,
    before_timestamp,
    timeframe,
    limit,
    currency,
    token,
    aggregate,
  }: {
    before_timestamp: number;
    network: string;
    pool_address: string;
    timeframe: 'day' | 'hour' | 'minute';
    aggregate: number;
    limit: number;
    currency: 'usd' | 'token';
    token?: 'base' | 'quote';
  }) => {
    type Return = {
      data: {
        id: string;
        type: string;
        attributes: {
          ohlcv_list: [number, number, number, number, number, number][];
        };
      };
    };

    try {
      const url = new URL(
        `https://api.geckoterminal.com/api/v2/networks/${network}/pools/${pool_address}/ohlcv/${timeframe}`,
      );
      const params = new URLSearchParams({
        aggregate: aggregate.toString(),
        before_timestamp: before_timestamp.toString(),
        limit: limit.toString(),
        currency,
      });

      if (token) {
        params.append('token', token);
      }

      url.search = params.toString();

      const response = await proxyAxios.get<Return>('/api/v1/proxy', {
        params: {
          url: url.toString(),
        },
      });

      return mapResponse<Return>({
        data: response.data,
      });
    } catch (error) {
      return mapError<Return>({ error });
    }
  };

  public static getSolCPMMCandles = async ({
    pairId,
    range,
    timeframe,
  }: {
    pairId: number;
    range: string;
    timeframe: SolCPMMTimeframe;
  }) => {
    try {
      const params = new URLSearchParams({ timeframe, range });
      const response = await ApiCharts.instance.get<IBaseResponse<IGetSolCPMMCandlesResponse>>(
        `/charts/sol-pairs/${pairId}/candles?${params.toString()}`,
      );

      return mapResponse<IGetSolCPMMCandlesResponse>({
        data: response.data,
      });
    } catch (error) {
      return mapError<IGetSolCPMMCandlesResponse>({ error });
    }
  };

  public static getSolMeteoraTrades = async ({
    pairId,
    limit = 50,
    cursor,
  }: {
    pairId: number;
    limit?: number;
    cursor?: string;
  }) => {
    try {
      const safeLimit = Number.isFinite(limit) ? Math.min(Math.max(Math.trunc(limit), 1), 100) : 50;
      const params = new URLSearchParams({
        limit: safeLimit.toString(),
      });
      if (cursor) {
        params.append('cursor', cursor);
      }

      const response = await ApiCharts.instance.get<IGetSolMeteoraTradesResponse>(
        `/charts/sol-pairs/${pairId}/trades?${params.toString()}`,
      );

      return mapResponse<IGetSolMeteoraTradesResponse>({
        data: response.data,
      });
    } catch (error) {
      return mapError<IGetSolMeteoraTradesResponse>({ error });
    }
  };

  /**
   * @deprecated Use `fetchCexChartKlines` from `src/entities/cex-volume/api/api.ts` instead.
   * This method will be removed in a future version.
   */
  public static getCexCharts = async ({
    symbol,
    cex,
    period,
    from,
    to,
    pair_id,
  }: {
    symbol: string;
    cex: ECexName[];
    period: string;
    from: number;
    to: number;
    pair_id: number[];
  }) => {
    type Return = { points: ICexChartsPoint[] };

    try {
      const urlParams = new URLSearchParams();

      urlParams.append('symbol', symbol);
      urlParams.append('period', period === '24h' ? '1d' : period === '168h' ? '1w' : period);
      urlParams.append('from', from.toString());
      urlParams.append('to', to.toString());

      if (cex) {
        for (const cexName of cex) {
          urlParams.append('cex', cexName.toString());
        }
      }

      if (pair_id) {
        for (const id of pair_id) {
          urlParams.append('pair_id', id.toString());
        }
      }

      const response = await metricsAxios.get(
        '/api/v1/balance/chart-klines?' + urlParams.toString(),
      );

      return mapResponse<Return>({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };

  public static getBalanceChart = async ({
    symbol,
    account_id,
    cex,
    period,
    from,
    to,
  }: {
    symbol: string;
    account_id: number[];
    cex: ECexName[];
    period: string;
    from: number;
    to: number;
  }) => {
    type Return = { points: BalanceChartPoint[] };

    try {
      const urlParams = new URLSearchParams();

      urlParams.append('symbol', symbol);
      urlParams.append('period', period === '24h' ? '1d' : period === '168h' ? '1w' : period);
      urlParams.append('from', from.toString());
      urlParams.append('to', to.toString());

      if (cex) {
        for (const cexName of cex) {
          urlParams.append('cex', cexName.toString());
        }
      }

      if (account_id) {
        for (const accountId of account_id) {
          urlParams.append('account_id', accountId.toString());
        }
      }

      const response = await metricsAxios.get(`/api/v1/balance/chart?${urlParams.toString()}`);

      return mapResponse<Return>({ data: response.data });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };

  public static getCexInternalVolumes = async ({
    pair_id,
    account_id,
    step_minutes,
    from,
    to,
  }: {
    pair_id: number;
    account_id?: number[];
    step_minutes: string;
    from: number;
    to: number;
  }) => {
    type Return = { points: { time: number; value: string }[] };

    try {
      const urlParams = new URLSearchParams();

      urlParams.append('pair_id', pair_id.toString());
      urlParams.append(
        'step_minutes',
        step_minutes === '24h' ? '1d' : step_minutes === '168h' ? '1w' : step_minutes,
      );
      urlParams.append('from', from.toString());
      urlParams.append('to', to.toString());

      if (account_id) {
        for (const accountId of account_id) {
          urlParams.append('account_id', accountId.toString());
        }
      }

      const response = await metricsAxios.get('/api/v1/chart/trade?' + urlParams.toString());

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };

  public static getCexStatisticsChartFee = async ({
    pairId,
    account_id,
    period,
    startDate,
    endDate,
  }: {
    pairId: number[];
    account_id?: number[];
    period?: string;
    startDate?: number;
    endDate?: number;
  }) => {
    type Return = { points: { time: number; value: string }[] };

    try {
      const urlParams = new URLSearchParams();

      if (pairId) {
        for (const id of pairId) {
          urlParams.append('pair_ids', id.toString());
        }
      }

      if (period) {
        urlParams.append('period', period);
      }
      if (!isNil(startDate) && !isNil(endDate)) {
        urlParams.append('from', startDate.toString());
        urlParams.append('to', endDate.toString());
      }
      if (account_id) {
        for (const accountId of account_id) {
          urlParams.append('account_id', accountId.toString());
        }
      }

      const response = await metricsAxios.get<Return>('/api/v2/chart/fee?' + urlParams.toString());

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };

  /**
   * @deprecated Use entities/project/hooks/useDeltaVolume.ts instead
   */
  public static getCexStatisticsChartDelta = async ({
    pairId,
    account_id,
    period,
    startDate,
    endDate,
  }: {
    pairId: number[];
    account_id?: number[];
    period?: string;
    startDate?: number;
    endDate?: number;
  }) => {
    type Return = {
      points: {
        time: number;
        buy_value_base: string;
        buy_value_quote: string;
        sell_value_base: string;
        sell_value_quote: string;
      }[];
    };

    try {
      const urlParams = new URLSearchParams();

      if (pairId) {
        for (const id of pairId) {
          urlParams.append('pair_id', id.toString());
        }
      }

      if (period) {
        urlParams.append('period', period);
      }
      if (!isNil(startDate) && !isNil(endDate)) {
        urlParams.append('from', startDate.toString());
        urlParams.append('to', endDate.toString());
      }
      if (account_id) {
        for (const accountId of account_id) {
          urlParams.append('account_id', accountId.toString());
        }
      }

      const response = await metricsAxios.get<Return>(
        '/api/v1/chart/delta?' + urlParams.toString(),
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };

  public static getCexStatisticsChartVolume = async ({
    pairId,
    account_id,
    period,
    startDate,
    endDate,
  }: {
    pairId: number[];
    account_id?: number[];
    period?: string;
    startDate?: number;
    endDate?: number;
  }) => {
    type Return = { points: { time: number; base: string; quote: string }[] };

    try {
      const urlParams = new URLSearchParams();

      if (pairId) {
        for (const id of pairId) {
          urlParams.append('pair_ids', id.toString());
        }
      }

      if (period) {
        urlParams.append('period', period);
      }
      if (!isNil(startDate) && !isNil(endDate)) {
        urlParams.append('from', startDate.toString());
        urlParams.append('to', endDate.toString());
      }
      if (account_id) {
        for (const accountId of account_id) {
          urlParams.append('account_id', accountId.toString());
        }
      }

      const response = await metricsAxios.get<Return>(
        '/api/v2/chart/volume?' + urlParams.toString(),
      );

      return mapResponse({ data: response.data });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };

  public static getCexStatisticsChartLiquidity = async ({
    symbol,
    cex,
    period,
    startDate,
    endDate,
    percentage,
  }: {
    symbol: string;
    cex: ECexName[];
    period?: string;
    startDate?: number;
    endDate?: number;
    percentage: number;
  }) => {
    type Return = {
      points: { time: number; value_in_base: string; value_in_quote: string }[];
    };

    try {
      const urlParams = new URLSearchParams();

      urlParams.append('symbol', symbol);
      urlParams.append('percentage', percentage.toString());

      if (cex) {
        for (const cexName of cex) {
          urlParams.append('cex', cexName.toString());
        }
      }

      if (period) {
        urlParams.append('period', period);
      }

      if (!isNil(startDate) && !isNil(endDate)) {
        urlParams.append('from', startDate.toString());
        urlParams.append('to', endDate.toString());
      }

      const response = await metricsAxios.get<Return>(
        '/api/v1/market/liquidity?' + urlParams.toString(),
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };

  public static getCexStatisticsChartSpread = async ({
    symbol,
    cex,
    period,
    startDate,
    endDate,
  }: {
    symbol: string;
    cex: ECexName[];
    period?: string;
    startDate?: number;
    endDate?: number;
  }) => {
    type Return = { points: { time: number; value: string }[] };

    try {
      const urlParams = new URLSearchParams();

      urlParams.append('symbol', symbol);

      if (cex) {
        for (const cexName of cex) {
          urlParams.append('cex', cexName.toString());
        }
      }

      if (period) {
        urlParams.append('period', period);
      }

      if (!isNil(startDate) && !isNil(endDate)) {
        urlParams.append('from', startDate.toString());
        urlParams.append('to', endDate.toString());
      }

      const response = await metricsAxios.get<Return>(
        '/api/v1/market/spread?' + urlParams.toString(),
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };

  public static getVWAPChart = async ({
    account_id,
    bots,
    period,
    from,
    to,
    pair_id,
  }: {
    account_id: number[];
    period?: string;
    from?: number;
    to?: number;
    pair_id: number[];
    bots: ECexBotFilter[];
  }) => {
    type Return = { points: ICexVWAPPoint[] };

    try {
      const urlParams = new URLSearchParams();

      if (period) {
        urlParams.append('period', period === '24h' ? '1d' : period === '168h' ? '1w' : period);
      }

      if (from && to) {
        urlParams.append('from', from.toString());
        urlParams.append('to', to.toString());
      }

      if (pair_id) {
        for (const id of pair_id) {
          urlParams.append('pair_id', id.toString());
        }
      }

      if (bots) {
        for (const bot of bots) {
          urlParams.append('bots', bot);
        }
      }

      if (account_id) {
        for (const accountId of account_id) {
          urlParams.append('account_id', accountId.toString());
        }
      }

      const response = await metricsAxios.get<Return>(
        '/api/v1/chart/vwapr?' + urlParams.toString(),
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };
}

export { ApiCharts };
